import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, ArrowLeft, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import Button from './Button';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: unknown[];
  level?: 'page' | 'global';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

/**
 * Error Boundary Component
 * Catches React errors and displays fallback UI
 *
 * Limitations (React Error Boundaries):
 * - Does NOT catch errors in event handlers (use try-catch)
 * - Does NOT catch errors in async code (setTimeout, promises)
 * - Does NOT catch errors during SSR
 * - Does NOT catch errors in the error boundary itself
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development mode
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state when resetKeys change
    if (
      hasError &&
      resetKeys &&
      prevProps.resetKeys &&
      resetKeys.length > 0 &&
      !this.areResetKeysEqual(prevProps.resetKeys, resetKeys)
    ) {
      this.resetError();
    }
  }

  areResetKeysEqual(prev: unknown[], next: unknown[]): boolean {
    if (prev.length !== next.length) return false;
    return prev.every((key, index) => key === next[index]);
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      showDetails: false
    });
  };

  toggleDetails = (): void => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails
    }));
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error, showDetails } = this.state;
    const { children, fallback, level = 'page' } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Render appropriate fallback based on level
      if (level === 'global') {
        return (
          <GlobalErrorFallback
            error={error}
            showDetails={showDetails}
            onToggleDetails={this.toggleDetails}
            onReload={this.handleReload}
          />
        );
      }

      return (
        <PageErrorFallback
          error={error}
          showDetails={showDetails}
          onToggleDetails={this.toggleDetails}
          onReload={this.handleReload}
        />
      );
    }

    return children;
  }
}

/**
 * Global Error Fallback
 * Full-page error display for app-level errors
 */
interface FallbackProps {
  error: Error;
  showDetails: boolean;
  onToggleDetails: () => void;
  onReload: () => void;
}

const GlobalErrorFallback: React.FC<FallbackProps> = ({ error, showDetails, onToggleDetails, onReload }) => {
  return (
    <div className={styles.errorBoundaryContainer}>
      <div className={styles.globalFallback}>
        <AlertTriangle size={64} className={styles.errorIcon} />
        <h1 className={styles.errorTitle}>Something went wrong</h1>
        <p className={styles.errorMessage}>
          We're sorry, but something unexpected happened. Please reload the page to try again.
        </p>

        <div className={styles.errorActions}>
          <Button variant="primary" onClick={onReload}>
            <RefreshCw size={16} />
            Reload Page
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className={styles.errorDetails}>
            <button className={styles.errorDetailsButton} onClick={onToggleDetails} type="button">
              {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showDetails ? 'Hide' : 'Show'} Error Details
            </button>
            {showDetails && (
              <div className={styles.errorDetailsContent}>
                <p className={styles.errorName}>
                  <strong>Error:</strong> {error.name}
                </p>
                <p className={styles.errorMessageDetail}>
                  <strong>Message:</strong> {error.message}
                </p>
                {error.stack && (
                  <pre className={styles.errorStack}>
                    <strong>Stack Trace:</strong>
                    {'\n'}
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Page Error Fallback
 * Page-level error display with navigation options
 */
const PageErrorFallback: React.FC<FallbackProps> = ({ error, showDetails, onToggleDetails, onReload }) => {
  return (
    <PageErrorFallbackWithNavigation
      error={error}
      showDetails={showDetails}
      onToggleDetails={onToggleDetails}
      onReload={onReload}
    />
  );
};

/**
 * Wrapper component to use navigate hook
 */
const PageErrorFallbackWithNavigation: React.FC<FallbackProps> = ({
  error,
  showDetails,
  onToggleDetails,
  onReload
}) => {
  const navigate = useNavigate();

  const handleGoBack = (): void => {
    window.history.back();
  };

  const handleGoToDashboard = (): void => {
    navigate('/dashboard');
  };

  return (
    <div className={styles.errorBoundaryContainer}>
      <div className={styles.pageFallback}>
        <AlertTriangle size={48} className={styles.errorIcon} />
        <h2 className={styles.errorTitle}>This page encountered an error</h2>
        <p className={styles.errorMessage}>
          Something went wrong while loading this page. You can try going back, returning to the dashboard, or
          reloading.
        </p>

        <div className={styles.errorActions}>
          <Button variant="secondary" onClick={handleGoBack}>
            <ArrowLeft size={16} />
            Go Back
          </Button>
          <Button variant="primary" onClick={handleGoToDashboard}>
            <Home size={16} />
            Go to Dashboard
          </Button>
          <Button variant="secondary" onClick={onReload}>
            <RefreshCw size={16} />
            Reload Page
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className={styles.errorDetails}>
            <button className={styles.errorDetailsButton} onClick={onToggleDetails} type="button">
              {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showDetails ? 'Hide' : 'Show'} Error Details
            </button>
            {showDetails && (
              <div className={styles.errorDetailsContent}>
                <p className={styles.errorName}>
                  <strong>Error:</strong> {error.name}
                </p>
                <p className={styles.errorMessageDetail}>
                  <strong>Message:</strong> {error.message}
                </p>
                {error.stack && (
                  <pre className={styles.errorStack}>
                    <strong>Stack Trace:</strong>
                    {'\n'}
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorBoundary;
